
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, DollarSign, Calendar, Play, Pause } from 'lucide-react';

interface ConversationMessage {
  role: string;
  content: string;
  datetime?: string;
}

interface Conversation {
  id: string;
  client_id: string;
  budget_eur: number;
  start_date: string;
  end_date: string;
  destination: string;
  summary: string;
  transcript: ConversationMessage[];
  closing_probability: number;
  last_updated: string;
  broker_notes: string;
  status: string;
  active: boolean;
  clients?: {
    full_name: string;
    email: string;
    phone: string;
    preferred_budget_eur: number;
    taste_notes: string;
  };
}

interface MetricsCardsProps {
  conversations: Conversation[];
}

export const MetricsCards = ({ conversations }: MetricsCardsProps) => {
  // Remove duplicates by client_id for accurate counts
  const uniqueConversations = React.useMemo(() => {
    const clientMap = new Map<string, Conversation>();
    
    conversations.forEach(conv => {
      const existingConv = clientMap.get(conv.client_id);
      if (!existingConv || new Date(conv.last_updated) > new Date(existingConv.last_updated)) {
        clientMap.set(conv.client_id, conv);
      }
    });
    
    return Array.from(clientMap.values());
  }, [conversations]);

  const totalConversations = uniqueConversations.length;
  const activeConversations = uniqueConversations.filter(conv => conv.active).length;
  const pausedConversations = uniqueConversations.filter(conv => !conv.active).length;

  const thisWeekConversations = uniqueConversations.filter(conv => {
    const lastUpdated = new Date(conv.last_updated);
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return lastUpdated >= oneWeekAgo;
  }).length;

  const metrics = [
    {
      title: "Total Conversations",
      value: totalConversations.toString(),
      icon: Users,
      gradient: "from-blue-500 to-blue-600",
      bgGradient: "from-blue-50 to-blue-100",
    },
    {
      title: "Active Conversations",
      value: activeConversations.toString(),
      icon: Play,
      gradient: "from-emerald-500 to-emerald-600",
      bgGradient: "from-emerald-50 to-emerald-100",
    },
    {
      title: "Paused Conversations",
      value: pausedConversations.toString(),
      icon: Pause,
      gradient: "from-orange-500 to-orange-600",
      bgGradient: "from-orange-50 to-orange-100",
    },
    {
      title: "This Week's Activity",
      value: thisWeekConversations.toString(),
      icon: Calendar,
      gradient: "from-indigo-500 to-indigo-600",
      bgGradient: "from-indigo-50 to-indigo-100",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {metrics.map((metric) => {
        const IconComponent = metric.icon;
        return (
          <Card key={metric.title} className={`relative overflow-hidden bg-gradient-to-br ${metric.bgGradient} border border-white/50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-700 tracking-wide">
                  {metric.title}
                </CardTitle>
                <div className={`p-3 rounded-xl bg-gradient-to-r ${metric.gradient} shadow-lg`}>
                  <IconComponent className="h-5 w-5 text-white" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-3xl font-bold text-slate-800 mb-1">
                {metric.value}
              </div>
            </CardContent>
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
          </Card>
        );
      })}
    </div>
  );
};
